<template>
    <PhGift :color="`url(#${gradientId})`" class="gift-glint-icon" aria-hidden="true">
        <defs>
            <linearGradient :id="gradientId" x1="0" y1="0" x2="1" y2="1">
                <stop v-for="stop in STOPS" :key="stop.offset" class="gift-glint-icon__stop" :offset="stop.offset" :style="stop.style" />
            </linearGradient>
        </defs>
    </PhGift>
</template>

<script setup lang="ts">
/**
 * The gift glyph in the rail's honey, crossed now and then by a glint: DESIGN.md, "The sanctioned
 * one-off". It is drawn at two sites, the rail's Free licenses entry and the lit front pass on the
 * Free License Credits plate, so the entry and the license it leads to read as one mark, honey at
 * rest, on hover and when active alike. The attrs a caller gives its glyph (size, weight, class)
 * fall through to PhGift, which draws its default slot first inside its `<svg>`, so the gradient
 * travels inside the glyph it fills.
 *
 * The glint is the gradient's own stops, not a second shape laid over the glyph: every stop rests
 * on --brand-rail, runs the same keyframes on its stop-color, and runs them one step behind the
 * stop before it, so a soft band of light enters at the box's top left corner, crosses the strokes
 * and leaves at the bottom right, then the glyph holds plain honey until the next cycle. The stops
 * run corner to corner, past both ends of the strokes (the bow is centered and the box's corner is
 * rounded), so the band fades in and out over empty corners rather than switching on mid-stroke.
 */
import { useId } from 'vue';
import { PhGift } from '@phosphor-icons/vue';

/** The stops the band crosses, spaced evenly from corner to corner of the glyph's box. */
const STOP_COUNT = 7;

/**
 * Each stop rests on the rail's honey in its own inline style and carries its index as `--stop`,
 * which the style block below turns into the stop's place in the sweep: every number the glint's
 * timing is made of lives there. The inline honey is what the glyph shows between glints, and
 * what it shows throughout under the design system's reduced-motion floor
 * (`packages/design-system/reset.css`): the floor cuts each stop to a single iteration of 0.01ms,
 * which with the delays below is over before the glyph is first drawn, and with no fill a stop
 * then falls back to the honey its own style rests on.
 */
const STOPS = Array.from({ length: STOP_COUNT }, (_, index) => ({
    offset: index / (STOP_COUNT - 1),
    style: { 'stop-color': 'var(--brand-rail)', '--stop': index },
}));

// A fill reaches its gradient by a document-wide id, so each glyph mints its own: two glyphs
// sharing one would both paint from whichever definition the document finds first.
const gradientId = useId();
</script>

<style lang="scss" scoped>
// The glint's timing, whole. One cycle every 8s: each stop holds honey, rises to the highlight
// and falls back between 25% and 31% of it, 480ms, and runs 160ms behind the stop before it, so
// the band takes about 1.4s from corner to corner and crosses the strokes in about a second. The
// first stop starts 1120ms into its cycle, so the first sweep reaches the strokes about a second
// after the glyph is drawn rather than after the cycle's full hold. The highlight is honey lifted
// halfway toward the rail's white: a shine on the honey rather than a white flash.
.gift-glint-icon__stop {
    animation: gift-glint 8s ease-in-out infinite;
    animation-delay: calc(var(--stop) * 160ms - 1120ms);
}

@keyframes gift-glint {
    0%,
    25%,
    31%,
    100% {
        stop-color: var(--brand-rail);
    }

    28% {
        stop-color: color-mix(in srgb, var(--brand-rail), var(--rail-ink) 50%);
    }
}

// The fill attribute is the gradient; under forced colors the glyph takes the system's text color
// instead, as every other glyph does, so the gradient and its glint are never painted there.
@media (forced-colors: active) {
    .gift-glint-icon {
        fill: currentColor;
    }
}
</style>
